import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'motion/react';
import { useRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { useMovingHighlight } from '@/shared/lib/useMovingHighlight';
import { useFieldContext } from '@/shared/ui/Field/FieldContext';

import styles from './Select.module.scss';

export type SelectSize = 'sm' | 'md' | 'lg';

const SIZE: Record<SelectSize, string> = {
  sm: styles['select--sm'] ?? '',
  md: styles['select--md'] ?? '',
  lg: styles['select--lg'] ?? '',
};

type RadixRootProps = ComponentPropsWithoutRef<typeof RadixSelect.Root>;

export interface SelectProps extends Omit<RadixRootProps, 'children'> {
  /**
   * Что показывать, пока ничего не выбрано.
   *
   * «Ничего не выбрано» — это пустая строка в `value`, а не `undefined`:
   * `undefined` означает «полем управляет сам список», и тогда выбранное
   * значение перестаёт возвращаться в форму.
   */
  placeholder?: string;
  selectSize?: SelectSize;
  /** Помечает поле ошибочным вне обёртки `Field`. */
  invalid?: boolean;
  id?: string;
  className?: string;
  /** Пункты: `SelectItem`, `SelectGroup`, `SelectSeparator`. */
  children: ReactNode;
}

/**
 * Выбор значения в форме.
 *
 * Не выпадающее меню: меню — это список действий, а поле — это значение.
 * Разница не косметическая. Поле связывается с подписью и с текстом ошибки,
 * участвует в отправке формы, объявляет себя скринридеру как `combobox` с
 * текущим значением, а не как набор пунктов, и запоминает выбранное.
 *
 * Поиска внутри нет намеренно. Список с полем ввода — это уже другой
 * элемент управления, с другой разметкой доступности; попытка сделать один
 * компонент и тем, и другим даёт control, плохой в обеих ролях. Пока
 * длинных списков нет, хватает перехода к пункту набором первых букв — он
 * работает из коробки.
 */
export function Select({
  placeholder = 'Не выбрано',
  selectSize = 'md',
  invalid,
  className,
  id,
  disabled,
  required,
  children,
  ...rest
}: SelectProps) {
  const field = useFieldContext();

  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const isRequired = required ?? field?.required;

  return (
    <RadixSelect.Root
      disabled={isDisabled}
      // Свойство подставляется, только когда оно есть: явный undefined
      // здесь означал бы не «неизвестно», а «не обязательно».
      {...(isRequired !== undefined && { required: isRequired })}
      {...rest}
    >
      <RadixSelect.Trigger
        id={id ?? field?.controlId}
        className={cx(
          styles['select'],
          SIZE[selectSize],
          isInvalid && styles['select--invalid'],
          className,
        )}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
      >
        {/* Значение показывает только подпись выбранного пункта: пояснение
            под ней нужно при выборе, но в свёрнутом поле оно превратилось бы
            во вторую строку и сломало высоту. */}
        <RadixSelect.Value
          placeholder={<span className={styles['select__placeholder']}>{placeholder}</span>}
        />

        <RadixSelect.Icon className={styles['select__icon']}>
          <ChevronDown size={15} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content className={styles['menu']} position="popper" sideOffset={6}>
          <RadixSelect.ScrollUpButton className={styles['menu__scroll']}>
            <ChevronUp size={14} />
          </RadixSelect.ScrollUpButton>

          <RadixSelect.Viewport className={styles['menu__viewport']}>
            <Highlighted>{children}</Highlighted>
          </RadixSelect.Viewport>

          <RadixSelect.ScrollDownButton className={styles['menu__scroll']}>
            <ChevronDown size={14} />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

/**
 * Обёртка пунктов с плывущей подложкой.
 *
 * Отдельный компонент, а не код в Select: хуку нужен ref на контейнер
 * пунктов, а он существует только пока список открыт.
 */
function Highlighted({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const box = useMovingHighlight(ref);

  return (
    <div ref={ref} className={styles['menu__list']}>
      {box && (
        <motion.span
          className={styles['menu__highlight']}
          initial={false}
          animate={{ y: box.top, height: box.height }}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  );
}

export type SelectItemProps = ComponentPropsWithoutRef<typeof RadixSelect.Item> & {
  /**
   * Пояснение под подписью.
   *
   * Ради него компонент и нужен в первую очередь: «редактор сайта» ничего
   * не говорит, пока рядом не написано, что именно этой роли можно.
   */
  description?: string;
  children: ReactNode;
};

export function SelectItem({ className, children, description, ...rest }: SelectItemProps) {
  return (
    <RadixSelect.Item className={cx(styles['menu__item'], className)} {...rest}>
      <span className={styles['menu__indicator']}>
        <RadixSelect.ItemIndicator>
          <Check size={14} />
        </RadixSelect.ItemIndicator>
      </span>

      <span className={styles['menu__text']}>
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>

        {description !== undefined && (
          <span className={styles['menu__description']}>{description}</span>
        )}
      </span>
    </RadixSelect.Item>
  );
}

export type SelectGroupProps = ComponentPropsWithoutRef<typeof RadixSelect.Group>;

export function SelectGroup(props: SelectGroupProps) {
  return <RadixSelect.Group {...props} />;
}

export type SelectLabelProps = ComponentPropsWithoutRef<typeof RadixSelect.Label>;

export function SelectLabel({ className, ...rest }: SelectLabelProps) {
  return <RadixSelect.Label className={cx(styles['menu__group-label'], className)} {...rest} />;
}

export type SelectSeparatorProps = ComponentPropsWithoutRef<typeof RadixSelect.Separator>;

export function SelectSeparator({ className, ...rest }: SelectSeparatorProps) {
  return <RadixSelect.Separator className={cx(styles['menu__separator'], className)} {...rest} />;
}
